import { useCallback, useEffect, useMemo, useState } from 'react'

import { getAutoGenerationConfigs } from '@/configs/aiFieldConfigs'
import type { AIFieldState } from '@/hooks/types/aiFieldGeneration'
import type { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import type { GetContentModuleResponse } from '@/services/api/api.types'
import { splitMediaSteps } from '@/utils/contentModuleForm'
import { isModuleFieldGenerated, type ProvenanceModuleField } from '@/utils/fieldProvenance'
import { getReadinessMessage, getStepsReadiness } from '@/utils/stepReadiness'

import type { CMFormSection } from './ContentModuleFormScreen'

const IDLE_HELPER = 'Generates the module fields below.'

type AIGeneration = ReturnType<typeof useAIFieldGeneration>

const plural = (n: number) => `${n} field${n === 1 ? '' : 's'}`

/**
 * Which module fields this pass may write — decision 8.
 *
 * Untouched means the field still holds the machine's text, or holds nothing at all.
 * Anything else is the expert's and is left exactly as it is.
 *
 * Provenance describes what is *stored*, so it only classifies a field the form still
 * agrees with. An unsaved divergence is something the marker cannot have seen, and the
 * safe reading of that is the expert's — a second press regenerates it once it is saved
 * and the backend has said whose it is.
 */
export function selectFieldsToGenerate(
  cm: Partial<GetContentModuleResponse> | undefined,
  values: Record<string, unknown>,
): string[] {
  return getAutoGenerationConfigs('cm')
    .map(config => config.formField)
    .filter(field => {
      const value = values[field]
      if (typeof value === 'string' && value.trim()) {
        const saved = cm?.[field as ProvenanceModuleField]
        if (typeof saved !== 'string' || saved.trim() !== value.trim()) return false
        return isModuleFieldGenerated(cm, field as ProvenanceModuleField)
      }
      return true
    })
}

export type GenerationResult = { total: number; succeeded: number; failed: number }

/**
 * What a pass did, counted rather than derived.
 *
 * `idle` is where success, failure and cancellation all come to rest, so the outcome is
 * the only thing that tells them apart — and a cancelled field has none. Reporting
 * `total - failed` as generated therefore claimed the Sidekick had written a field the
 * expert had stopped, which was the one number on this button nobody could check.
 */
export function summarizeGeneration(outcomes: Array<AIFieldState['lastOutcome']>): GenerationResult {
  return {
    total: outcomes.length,
    succeeded: outcomes.filter(outcome => outcome === 'succeeded').length,
    failed: outcomes.filter(outcome => outcome === 'failed').length,
  }
}

/** The button's helper text for a finished pass. */
export function generationReport(result: GenerationResult): string {
  if (result.total === 0) return 'Nothing to generate — every field below already has content.'
  // Nothing succeeded and nothing failed: every field was cancelled.
  if (result.succeeded === 0 && result.failed === 0) return 'Generation cancelled. Nothing was changed.'
  if (result.succeeded === 0) return 'Nothing could be generated. Please try again.'
  if (result.failed > 0) {
    return `Generated ${plural(result.succeeded)}; ${result.failed} couldn't be. Anything you wrote was kept.`
  }
  return `Generated ${plural(result.succeeded)}. Anything you wrote was kept.`
}

export type DoneAddingFiles = ReturnType<typeof useDoneAddingFiles>

/**
 * "Done Adding Files" — decisions 8 and 15.
 *
 * Fills the module's generated fields in one pass once every file is ready, and leaves
 * alone anything the expert wrote. What counts as theirs is the saved provenance
 * (C2-2597), so a field they rewrote survives this and every later refresh alike.
 *
 * It stays clickable afterwards. Courtney confirmed a second press should regenerate
 * what is still the machine's, which is the same rule applied twice rather than a
 * one-shot action.
 */
export function useDoneAddingFiles(props: CMFormSection, ai: AIGeneration) {
  // The pass in flight, then what it did. Held apart because this hook shares its
  // generation instance with the per-field "Ask Sidekick" buttons: reading live state to
  // describe a finished pass let an unrelated later request reopen and rewrite its report.
  const [inFlight, setInFlight] = useState<string[] | null>(null)
  const [result, setResult] = useState<GenerationResult | null>(null)

  const steps = props.stepsQuery.data
  // Resolved the way every other consumer resolves it, dangling-id fallback included, so
  // the message cannot name a different file from the one sitting in the Primary slot.
  const primaryStepId = useMemo(
    () => splitMediaSteps(steps, props.cmQuery.data?.primaryStepId).primary?.stepId,
    [steps, props.cmQuery.data?.primaryStepId],
  )
  const readiness = useMemo(() => getStepsReadiness(steps ?? [], primaryStepId), [steps, primaryStepId])

  const running = !!inFlight

  // `idle` is where success and failure both come to rest, so what happened is read from
  // the outcome. Recorded once, when the last field settles.
  useEffect(() => {
    if (!inFlight) return
    if (!inFlight.every(field => ai.getFieldState(field, 'cm').state === 'idle')) return

    setResult(summarizeGeneration(inFlight.map(field => ai.getFieldState(field, 'cm').lastOutcome)))
    setInFlight(null)
  }, [inFlight, ai])

  const generate = useCallback(() => {
    const fields = selectFieldsToGenerate(props.cmQuery.data, props.moduleForm.getValues())
    if (!fields.length) {
      // Straight to the result, or the button flashes "Generating 0 fields…" on its way.
      setResult({ total: 0, succeeded: 0, failed: 0 })
      return
    }
    setResult(null)
    setInFlight(fields)
    fields.forEach(field => ai.generateFieldAndAccept('cm', field))
  }, [ai, props.cmQuery.data, props.moduleForm])

  // Memoized: the screen holds this object and feeds it back into the sections memo.
  return useMemo(() => {
    const blocked = getReadinessMessage(readiness, { statusesUnavailable: props.stepsQuery.isStatusError })
    const helper = blocked
      ? blocked
      : inFlight
        ? `Generating ${plural(inFlight.length)}…`
        : !result
          ? IDLE_HELPER
          : generationReport(result)

    return {
      // Her doc gates this on any file existing, not a supplementary one.
      visible: (steps?.length ?? 0) > 0,
      disabled: !readiness.allReady || running,
      running,
      helper,
      generate,
    }
  }, [readiness, running, inFlight, result, steps?.length, generate, props.stepsQuery.isStatusError])
}
